import React, { useState, useEffect, useCallback } from 'react';
import { Search, User, CheckCircle, XCircle, Loader, SlidersHorizontal } from 'lucide-react';
import api from '../services/api';

const ROLE_LABELS = {
  officer: 'Recovery Officer',
  manager: 'Manager',
  technician: 'Technician',
  clerk: 'Clerk',
};

const ROLE_BADGE = {
  officer:    'bg-blue-100 text-blue-700',
  manager:    'bg-purple-100 text-purple-700',
  technician: 'bg-amber-100 text-amber-700',
  clerk:      'bg-green-100 text-green-700',
};

const ROLE_TABS = ['all', 'officer', 'manager', 'technician', 'clerk'];

const PERMISSION_GROUPS = {
  officer: [
    {
      label: 'Dashboard Sections',
      keys: [
        { key: 'show_dashboard_stats',       label: 'Stats & Summary Cards' },
        { key: 'show_expired_customers',      label: 'Expired Customers List' },
        { key: 'show_today_expiring',         label: 'Today Expiring List' },
        { key: 'show_tomorrow_expiring',      label: 'Tomorrow Expiring List' },
        { key: 'show_missed_renewals',        label: 'Missed Renewals Section' },
{ key: 'show_uncollected',            label: 'Uncollected This Month' },
      ],
    },
    {
      label: 'Actions',
      keys: [
        { key: 'can_add_collections',         label: 'Log New Collections' },
        { key: 'can_view_collection_history', label: 'View Collection History' },
        { key: 'can_view_customer_details',   label: 'Open Customer Details' },
      ],
    },
  ],
  manager: [
    {
      label: 'Inventory',
      keys: [
        { key: 'show_stock',        label: 'Stock Management' },
        { key: 'can_approve_stock', label: 'Approve / Reject Stock Requests' },
      ],
    },
    {
      label: 'Operations',
      keys: [
        { key: 'show_activations',    label: 'Daily Activations Access' },
        { key: 'can_add_activations', label: 'Add Activation Entries' },
      ],
    },
    {
      label: 'Finance',
      keys: [
        { key: 'show_expenses', label: 'Daily Expenses & Recovery' },
        { key: 'show_tools',    label: 'Import Tools (Panel Data Import)' },
      ],
    },
  ],
  technician: [
    {
      label: 'Complaints',
      keys: [
        { key: 'show_complaints',       label: 'View Complaints List & Dashboard' },
        { key: 'can_update_complaints', label: 'Update Complaint Status / Progress' },
        { key: 'can_close_complaints',  label: 'Close / Resolve Complaints' },
      ],
    },
    {
      label: 'Customers',
      keys: [
        { key: 'can_view_customers', label: 'Search & View Customers' },
      ],
    },
  ],
  clerk: [
    {
      label: 'Operations',
      keys: [
        { key: 'show_activations',    label: 'Daily Activations Access' },
        { key: 'can_add_activations', label: 'Add Activation Entries' },
        { key: 'show_billing',        label: 'Billing & Invoices' },
      ],
    },
    {
      label: 'Support',
      keys: [
        { key: 'show_complaints',   label: 'View Complaints List' },
        { key: 'can_add_complaints', label: 'Add & Assign Complaints' },
      ],
    },
  ],
};

const Toggle = ({ checked, onChange, saving }) => (
  <button
    type="button"
    onClick={onChange}
    disabled={saving}
    aria-checked={checked}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
      checked ? 'bg-blue-600' : 'bg-gray-300'
    } ${saving ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-4.5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

export default function FeaturePermissions() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleTab, setRoleTab] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    api.get('/admin/feature-permissions/users/')
      .then(res => setUsers(res.data))
      .catch(() => showToast('Failed to load users', 'error'))
      .finally(() => setLoadingUsers(false));
  }, []);

  const selectUser = useCallback((u) => {
    setSelectedUser(u);
    setLoadingPerms(true);
    api.get(`/admin/feature-permissions/users/${u.id}/`)
      .then(res => setPermissions(res.data.permissions))
      .catch(() => showToast('Failed to load permissions', 'error'))
      .finally(() => setLoadingPerms(false));
  }, []);

  const togglePerm = async (key) => {
    const prev = { ...permissions };
    const newVal = permissions[key] !== false;  // if currently true → set false, else → set true
    const updated = { ...permissions, [key]: !newVal };
    setPermissions(updated);
    setSavingKey(key);
    try {
      const res = await api.patch(`/admin/feature-permissions/users/${selectedUser.id}/`, {
        permissions: { [key]: !newVal },
      });
      setPermissions(res.data.permissions);
      showToast(!newVal ? 'Feature enabled' : 'Feature disabled');
    } catch {
      setPermissions(prev);
      showToast('Failed to save — try again', 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const filtered = users.filter(u => {
    const matchTab = roleTab === 'all' || u.role === roleTab;
    const q = search.toLowerCase();
    const matchQ = !q || u.username.toLowerCase().includes(q) || u.full_name.toLowerCase().includes(q);
    return matchTab && matchQ;
  });

  const groups = selectedUser ? (PERMISSION_GROUPS[selectedUser.role] || []) : [];
  const totalPerms = groups.reduce((s, g) => s + g.keys.length, 0);
  const enabledPerms = groups.reduce((s, g) => s + g.keys.filter(({ key }) => permissions[key] !== false).length, 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-semibold transition-all ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {toast.type === 'error' ? <XCircle size={15} /> : <CheckCircle size={15} />}
          {toast.msg}
        </div>
      )}

      {/* Page Header */}
      <div className="mb-6">
        <h2 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
          <span className="w-1 h-6 bg-blue-600 rounded-full"></span>
          Feature Access Control
        </h2>
        <p className="text-sm text-gray-500 ml-3">
          Select any user and toggle which features they can access. Changes take effect on their next login.
        </p>
      </div>

      {/* Role Tab Bar */}
      <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
        {ROLE_TABS.map(r => (
          <button
            key={r}
            onClick={() => { setRoleTab(r); setSelectedUser(null); }}
            className={`flex items-center gap-1.5 pb-3 px-3 text-sm font-medium whitespace-nowrap transition-all border-b-2 -mb-px ${
              roleTab === r
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {r === 'all' ? 'All Users' : ROLE_LABELS[r]}
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
              roleTab === r ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {r === 'all' ? users.length : users.filter(u => u.role === r).length}
            </span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* LEFT — User List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

            {/* Search */}
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or username…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                />
              </div>
            </div>

            {/* List */}
            <div className="overflow-y-auto max-h-[540px] divide-y divide-gray-50">
              {loadingUsers ? (
                <div className="flex items-center justify-center py-12">
                  <Loader size={20} className="animate-spin text-gray-400" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-12 text-center">
                  <User size={28} className="mx-auto text-gray-200 mb-2" />
                  <p className="text-sm text-gray-400">No users found</p>
                </div>
              ) : (
                filtered.map(u => (
                  <button
                    key={u.id}
                    onClick={() => selectUser(u)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      selectedUser?.id === u.id
                        ? 'bg-blue-50 border-l-2 border-l-blue-600'
                        : 'border-l-2 border-l-transparent'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                      selectedUser?.id === u.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {(u.full_name || u.username).charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-semibold truncate ${selectedUser?.id === u.id ? 'text-blue-700' : 'text-gray-800'}`}>
                        {u.full_name}
                      </p>
                      <p className="text-xs text-gray-400 truncate">@{u.username}</p>
                    </div>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${ROLE_BADGE[u.role] || 'bg-gray-100 text-gray-600'}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* RIGHT — Permission Toggles */}
        <div className="lg:col-span-3">
          {!selectedUser ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center justify-center py-24 text-center px-6">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                <SlidersHorizontal size={24} className="text-gray-400" />
              </div>
              <p className="text-sm font-semibold text-gray-600 mb-1">No user selected</p>
              <p className="text-xs text-gray-400">Pick a user from the list to manage their feature access</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

              {/* Selected User Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                    {(selectedUser.full_name || selectedUser.username).charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{selectedUser.full_name}</p>
                    <p className="text-xs text-gray-400">
                      @{selectedUser.username}
                      <span className={`ml-2 font-semibold px-1.5 py-0.5 rounded-full text-[11px] ${ROLE_BADGE[selectedUser.role]}`}>
                        {ROLE_LABELS[selectedUser.role]}
                      </span>
                    </p>
                  </div>
                </div>
                {!loadingPerms && (
                  <div className="text-right">
                    <p className="text-xl font-bold text-gray-900">{enabledPerms}<span className="text-sm font-normal text-gray-400">/{totalPerms}</span></p>
                    <p className="text-xs text-gray-400">features active</p>
                  </div>
                )}
              </div>

              {/* Permission Groups */}
              {loadingPerms ? (
                <div className="flex items-center justify-center py-16">
                  <Loader size={22} className="animate-spin text-blue-500" />
                </div>
              ) : (
                <div>
                  {groups.length === 0 ? (
                    <div className="py-12 text-center text-sm text-gray-400">
                      No configurable permissions for this role.
                    </div>
                  ) : (
                    groups.map((group, gi) => (
                      <div key={group.label} className={`px-6 py-5 ${gi < groups.length - 1 ? 'border-b border-gray-100' : ''}`}>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                          {group.label}
                        </p>
                        <div className="space-y-4">
                          {group.keys.map(({ key, label }) => {
                            const isEnabled = permissions[key] !== false;
                            const isSaving = savingKey === key;
                            return (
                              <div key={key} className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isEnabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                                  <div>
                                    <p className="text-sm font-medium text-gray-800">{label}</p>
                                    <p className={`text-xs font-medium ${isEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                                      {isEnabled ? 'Allowed' : 'Blocked'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {isSaving && <Loader size={13} className="animate-spin text-gray-400" />}
                                  <Toggle
                                    checked={isEnabled}
                                    onChange={() => !isSaving && togglePerm(key)}
                                    saving={isSaving}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}

                  <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
                    <p className="text-xs text-gray-400">
                      Changes save instantly. User must re-login for updated permissions to take effect.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
